import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/game/config';
import { initI18n } from '@/i18n';
import { initPlatform } from '@/platform';
import { initSwUpdate } from '@/platform/swUpdate';
import { initCountryFlagFont } from '@/platform/countryFlagFont';
import { initAudioUnlock } from '@/game/audio';
import { initAnalytics } from '@/analytics';
import { devFlag } from '@/game/dev';
import { resetClipTrial } from '@/game/clip';
import { bindQrPromoToScenes } from '@/game/ui/qrOverlay';
import { BootScene } from '@/game/scenes/BootScene';
import { PreloadScene } from '@/game/scenes/PreloadScene';
import { MenuScene } from '@/game/scenes/MenuScene';
import { RoundScene } from '@/game/scenes/RoundScene';
import { BackdropScene } from '@/game/scenes/BackdropScene';
import { BoardScene } from '@/game/scenes/BoardScene';
import { HUDScene } from '@/game/scenes/HUDScene';
import { HelpScene } from '@/game/scenes/HelpScene';
import { SummaryScene } from '@/game/scenes/SummaryScene';
import { CelebrationScene } from '@/game/scenes/CelebrationScene';
import { SurvivalScene } from '@/game/scenes/SurvivalScene';
import { PairsScene } from '@/game/scenes/PairsScene';
import { MazeScene } from '@/game/scenes/MazeScene';
import { LabyrinthScene } from '@/game/scenes/LabyrinthScene';
import { WagonScene } from '@/game/scenes/WagonScene';
import { RecipeScene } from '@/game/scenes/RecipeScene';
import { CatScene } from '@/game/scenes/CatScene';
import { WormScene } from '@/game/scenes/WormScene';
import { RocketReadScene } from '@/game/scenes/RocketReadScene';

async function main() {
  await initI18n();
  initPlatform();
  initSwUpdate();   // startup-only update check, applied later from the Menu (no mid-round reloads)
  initCountryFlagFont();   // on Windows/Chromium (no native flag glyphs) load a local flag webfont
  initAudioUnlock();   // prime SpeechSynthesis + Web Audio on the first gesture (mobile needs this)
  // Web demo (?demo) ONLY, and lazily — outside the demo this fetches nothing and the pixel chunk
  // is never downloaded (src/analytics/index.ts). Not awaited: measurement never delays the game.
  void initAnalytics();

  // `?trialreset` wipes the trial counters so the demo can be replayed from board 1. Testing the
  // capped flow otherwise means hand-editing localStorage, and counters written under an older,
  // larger allowance (the clip was once 10 boards) leave a lesson permanently spent. devFlag() is
  // inert on production, so this can only ever be used on a dev or staging host.
  if (devFlag('trialreset')) resetClipTrial();

  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game',
    backgroundColor: '#0b0d10',
    // Battery: cap rendering at 60fps — without a limit Phaser runs at the display refresh rate,
    // so 90/120Hz phones would do 1.5–2× the render work for no visual benefit in a word game.
    fps: { limit: 60 },
    // Battery: prefer the power-efficient GPU for the WebGL context.
    render: { powerPreference: 'low-power' },
    // Give up on an asset that never answers. Phaser's default is no timeout at all, so a request
    // that hangs — a wedged connection, a proxy that swallows it — leaves the scene waiting in
    // LOADING forever, and a scene in LOADING renders NOTHING: an empty board with no backdrop and
    // no words, for as long as the child looks at it. A timed-out file is reported as a failed one
    // instead, the loader completes, create() runs, and the fallbacks that already exist take over
    // (a starfield for a missing backdrop, the text banner for a missing picture).
    //
    // 15 s is deliberately far above any honest load. The file that would actually hurt to lose is
    // a picture board's question icon, and those are 3.5 KB each — they arrive even on 2G. The one
    // asset big enough to be at risk is the backdrop (211 KB average), and losing that costs a
    // starfield, not a playable board. In an installed app none of this is on the network at all:
    // the service worker has both from the precache.
    loader: { timeout: 15000 },
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: GAME_WIDTH,
      height: GAME_HEIGHT,
    },
    // DOM layer for native HTML controls (the menu's settings buttons) — canvas hit-testing was
    // unreliable on touch, so those are real <button>s positioned in game space over the canvas.
    dom: { createContainer: true },
    // Order matters: scenes render in this order, so Backdrop must come before Board (and Board
    // before HUD) for the round's three layers to stack the way they look.
    scene: [BootScene, PreloadScene, MenuScene, RoundScene, BackdropScene, BoardScene, HUDScene, HelpScene, SummaryScene, CelebrationScene, SurvivalScene, PairsScene, MazeScene, WagonScene, LabyrinthScene, RecipeScene, CatScene, WormScene, RocketReadScene],
  });

  // The DOM container overlays the whole canvas; make it click-through so only its own elements
  // (which set pointer-events: auto) capture input — everything else falls through to the canvas.
  if (game.domContainer) game.domContainer.style.pointerEvents = 'none';

  // The small "get the app" QR card in the bottom-left corner — desktop web only, only once this
  // browser has finished a round, and only on the calm screens (src/game/ui/qrOverlay.ts). Wired
  // here because it is the one piece of UI that belongs to the PAGE rather than to a scene: it
  // outlives every scene change, so it subscribes to them instead of being built by one.
  bindQrPromoToScenes(game);

  // First-load scale race: on a phone the browser sometimes reports a stale/transitional viewport
  // (before the landscape/safe-area layout settles), so the FIT canvas is positioned wrong and the
  // bottom row (stars + mute button) ends up under the system bar until a reload. Re-fit once the
  // layout has settled and again on orientation change so it self-corrects without a reload.
  const refit = (): void => { game.scale.refresh(); };
  requestAnimationFrame(refit);
  window.setTimeout(refit, 300);
  window.addEventListener('orientationchange', () => window.setTimeout(refit, 100));

  // Expose for dev/test tooling (puppeteer scripts in /tools). Strip in prod if/when needed.
  (window as unknown as { __phaser: Phaser.Game }).__phaser = game;
}

main();
